// This composable is used to setup and provide the identity fields for the form.
import type { V4FuneralHome } from '@/apiTypes'
import Plan from '@/classes/Plan'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import { useSessionStore } from '@/stores/session'
import type { SalesContact } from '@/types'
import { DateTime } from 'luxon'

let authStore: ReturnType<typeof useAuthStore> | null = null
let crmStore: ReturnType<typeof useCrmStore> | null = null
let sessionStore: ReturnType<typeof useSessionStore> | null = null
let funeralHome: V4FuneralHome | undefined = undefined
let planHolder: Plan | undefined = undefined
let planHolderId: string | undefined = undefined
let salesContact: SalesContact | undefined = undefined

export const useIdentitySetup = () => {
	const identitySetup = (id: string, entities?: string[]) => {
		planHolderId = id
		authStore = useAuthStore()
		sessionStore = useSessionStore()
		crmStore = useCrmStore()

		if (entities?.includes('planHolder')) {
			setPlanHolder()
		}

		if (entities?.includes('funeralHome')) {
			setFuneralHome()
		}

		if (entities?.includes('salesContact')) {
			setSalesContact()
		}
	}

	const setPlanHolder = () => {
		if (!planHolderId || !sessionStore || !crmStore || !authStore) {
			throw new Error('Please run setup first')
		}

		planHolder = sessionStore?.getPlanHolder(planHolderId)
	}

	const setFuneralHome = () => {
		if (!planHolderId || !sessionStore || !crmStore || !authStore) {
			throw new Error('Please run setup first')
		}

		if (!planHolder) {
			setPlanHolder()
		}

		funeralHome = crmStore?.findFuneralHome((planHolder as Plan).funeralHomeId)
	}

	const setSalesContact = () => {
		if (!authStore) {
			throw new Error('Please run setup first')
		}

		salesContact = authStore.user?.salesContact
	}

	return {
		identitySetup,
		setPlanHolder,
		setFuneralHome,
		setSalesContact,
	}
}

export const useIdentityFields = () => {
	const funeralHomeName = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.name,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeFullAddress = (fieldName: string) => [
		{
			name: fieldName,
			value: [
				funeralHome?.billingAddress?.street,
				funeralHome?.billingAddress?.city,
				funeralHome?.billingAddress?.state,
				funeralHome?.billingAddress?.postalCode,
			].join(', '),
			format: false,
			section: 'identity',
		},
	]

	const funeralHomePhone = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.phoneNumber,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeZip = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.billingAddress?.postalCode,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeState = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.billingAddress?.state,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeCity = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.billingAddress?.city,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeStreet = (fieldName: string) => [
		{
			name: fieldName,
			value: funeralHome?.billingAddress?.street,
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeCityState = (fieldName: string) => [
		{
			name: fieldName,
			value: [
				funeralHome?.billingAddress?.city,
				funeralHome?.billingAddress?.state,
			].join(', '),
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeCityStateZip = (fieldName: string) => [
		{
			name: fieldName,
			value: [
				funeralHome?.billingAddress?.city,
				funeralHome?.billingAddress?.state,
				funeralHome?.billingAddress?.postalCode,
			].join(', '),
			format: false,
			section: 'identity',
		},
	]

	const funeralHomeRepresentativeProducerNumber = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insuranceProducerNumber?.producerNumber ?? '',
			format: false,
			section: 'identity',
		},
	]

	const getPurchaser = () => {
		if (!planHolder) {
			throw new Error('Please run setup first')
		}

		const purchaser = planHolder?.ownerIsInsured
			? planHolder
			: planHolder?.policyOwner

		// TODO: Revisit and refactor.
		// Written to clear purchaser fields.
		return {
			...purchaser,
			firstName: purchaser?.firstName ?? '',
			middleName: purchaser?.middleName ?? '',
			lastName: purchaser?.lastName ?? '',
			birthDate: purchaser?.birthDate ?? '',
			socialSecurityNumber: purchaser?.socialSecurityNumber ?? '',
			address: purchaser?.address ?? '',
			city: purchaser?.city ?? '',
			state: purchaser?.state ?? '',
			zip: purchaser?.zip ?? '',
			email: purchaser?.email ?? '',
			phone: purchaser?.phone ?? '',
			secondaryPhone: purchaser?.secondaryPhone ?? '',
		}
	}

	const policyOwnerFields = (fields: Record<string, string>) => {
		// If the policy owner is the insured, the purchaser fields are empty.
		if (planHolder?.ownerIsInsured) {
			return Object.values(fields).map((fieldName: keyof typeof fields) => ({
				name: fieldName,
				value: '',
				format: false,
				section: 'identity',
			}))
		}

		const result = [
			...purchaserFirstName(fields.purchaserFirstName),
			...purchaserMiddleInitial(fields.purchaserMiddleInitial),
			...purchaserLastName(fields.purchaserLastName),
			...purchaserRelationship(fields.purchaserRelationship),
			...purchaserSSN(fields.purchaserSSN),
		]

		return result
	}

	const conditionalInsuredFields = (fields: Record<string, string>) => {
		// If the purchaser is the insured, the insured fields are empty.
		if (planHolder?.ownerIsInsured) {
			return Object.values(fields).map((fieldName: keyof typeof fields) => ({
				name: fieldName,
				value: '',
				format: false,
				section: 'identity',
			}))
		}

		const result = [
			...insuredFullName(fields.insuredFullName),
			...insuredFullAddress(fields.insuredFullAddress),
		]

		return result
	}

	const purchaserFirstName = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().firstName,
			format: false,
			section: 'identity',
		},
	]

	const purchaserMiddleInitial = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().middleName?.slice(0, 1) ?? '',
			format: false,
			section: 'identity',
		},
	]

	const purchaserLastName = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().lastName,
			format: false,
			section: 'identity',
		},
	]

	const purchaserFullName = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().middleName
				? `${getPurchaser().firstName} ${getPurchaser().middleName} ${getPurchaser().lastName}`
				: `${getPurchaser().firstName} ${getPurchaser().lastName}`,
			format: false,
			section: 'identity',
		},
	]

	const purchaserRelationship = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.beneficiaryRelationship,
			format: false,
			section: 'identity',
		},
	]

	const purchaserEmail = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().email,
			format: false,
			section: 'identity',
		},
	]

	const purchaserPhone = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().phone,
			format: false,
			section: 'identity',
		},
	]

	const purchaserSecondaryPhone = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().secondaryPhone,
			format: false,
			section: 'identity',
		},
	]

	const purchaserZip = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().zip,
			format: false,
			section: 'identity',
		},
	]

	const purchaserState = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().state,
			format: false,
			section: 'identity',
		},
	]

	const purchaserCity = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().city,
			format: false,
			section: 'identity',
		},
	]

	const purchaserAddress = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().address,
			format: false,
			section: 'identity',
		},
	]

	const purchaserFullAddress = (fieldName: string) => [
		{
			name: fieldName,
			value: [
				getPurchaser().address,
				getPurchaser().city,
				getPurchaser().state,
			].join(', '),
			format: false,
			section: 'identity',
		},
	]

	const purchaserCityState = (fieldName: string) => [
		{
			name: fieldName,
			value: `${getPurchaser().city}, ${getPurchaser().state}`,
			format: false,
			section: 'identity',
		},
	]

	const purchaserSSN = (fieldName: string) => [
		{
			name: fieldName,
			value: getPurchaser().socialSecurityNumber,
			format: false,
			section: 'identity',
		},
	]

	const insuredFullName = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.middleName
				? `${planHolder?.firstName} ${planHolder?.middleName} ${planHolder?.lastName}`
				: `${planHolder?.firstName} ${planHolder?.lastName}`,
			format: false,
			section: 'identity',
		},
	]

	const insuredFullAddress = (fieldName: string) => {
		if (!planHolder) {
			throw new Error('Please run setup first')
		}

		return [
			{
				name: fieldName,
				value: [planHolder.address, planHolder.city, planHolder.state]
					.filter(Boolean)
					.join(', '),
				format: false,
				section: 'identity',
			},
		]
	}

	const agentName = (fieldName: string) => {
		const agentName =
			(
				salesContact?.legalName ??
				salesContact?.name ??
				`${salesContact?.firstname ?? ''} ${salesContact?.lastname ?? ''}`
			).trim() ?? ''

		return [
			{
				name: fieldName,
				value: agentName,
				format: false,
				section: 'identity',
			},
		]
	}

	const funeralHomeRepresentativePrintedName = (fieldName: string) =>
		agentName(fieldName)

	const agentProducerNumber = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.oapProducerNumber?.producerNumber ?? '',
			format: false,
			section: 'identity',
		},
	]

	const insuredFirstName = (fieldName: string) => [
		{
			name: fieldName,
			value: `${planHolder?.firstName}`,
			format: false,
			section: 'identity',
		},
	]

	const insuredMiddleInitial = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.middleName?.slice(0, 1) ?? '',
			format: false,
			section: 'identity',
		},
	]

	const insuredLastName = (fieldName: string) => [
		{
			name: fieldName,
			value: `${planHolder?.lastName}`,
			format: false,
			section: 'identity',
		},
	]

	const insuredMaleCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.gender === 'male' ? 'Yes' : 'Off',
			format: false,
			section: 'identity',
		},
	]

	const insuredFemaleCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.gender === 'female' ? 'Yes' : 'Off',
			format: false,
			section: 'identity',
		},
	]

	const insuredDateOfBirth = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.birthDate
				? DateTime.fromISO(planHolder?.birthDate).toFormat('M/d/yyyy')
				: undefined,
			format: false,
			section: 'identity',
		},
	]

	// Helper to calculate the age of the proposedInsured (date considered, used in enrolment)
	const calculateProposedInsuredAge = (): string | undefined => {
		if (!planHolder?.birthDate) return undefined

		const birthDate = DateTime.fromISO(planHolder?.birthDate)
		const today = DateTime.now()

		return Math.floor(today.diff(birthDate, 'years').years).toString()
	}

	const insuredAge = (fieldName: string) => [
		{
			name: fieldName,
			value: calculateProposedInsuredAge(),
			format: false,
			section: 'identity',
		},
	]

	const insuredPrimaryPhone = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.phone,
			format: false,
			section: 'identity',
		},
	]

	const insuredSSN = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.socialSecurityNumber,
			format: false,
			section: 'identity',
		},
	]

	const insuredEmail = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.email,
			format: false,
			section: 'identity',
		},
	]

	const insuredHospiceYesOrNo = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insurance.terminallyIll ? 'Yes' : 'No',
			format: false,
			section: 'identity',
		},
	]

	const beneficiaryName = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.beneficiary,
			format: false,
			section: 'identity',
		},
	]
	const beneficiaryRelationship = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.beneficiaryRelationship,
			format: false,
			section: 'identity',
		},
	]

	const dateDay = (fieldName: string) => [
		{
			name: fieldName,
			value: DateTime.now().toFormat('dd'),
			format: false,
			section: 'identity',
		},
	]
	const dateMonth = (fieldName: string) => [
		{
			name: fieldName,
			value: DateTime.now().toFormat('MMMM'),
			format: false,
			section: 'identity',
		},
	]
	const dateYear = (fieldName: string) => [
		{
			name: fieldName,
			value: DateTime.now().toFormat('yy'),
			format: false,
			section: 'identity',
		},
	]
	const dateFields = (fields: Record<string, string>) => [
		...dateDay(fields.day),
		...dateMonth(fields.month),
		...dateYear(fields.year),
	]

	return {
		funeralHomeName,
		funeralHomeFullAddress,
		funeralHomeStreet,
		funeralHomePhone,
		funeralHomeZip,
		funeralHomeCity,
		funeralHomeState,
		funeralHomeCityState,
		funeralHomeCityStateZip,
		funeralHomeRepresentativeProducerNumber,
		funeralHomeRepresentativePrintedName,
		purchaserFirstName,
		purchaserMiddleInitial,
		purchaserLastName,
		purchaserFullName,
		purchaserRelationship,
		purchaserSSN,
		purchaserSecondaryPhone,
		purchaserPhone,
		purchaserZip,
		purchaserState,
		purchaserCity,
		purchaserCityState,
		purchaserEmail,
		purchaserAddress,
		purchaserFullAddress,
		policyOwnerFields,
		insuredFullName,
		insuredFullAddress,
		insuredFirstName,
		insuredMiddleInitial,
		insuredLastName,
		insuredMaleCheck,
		insuredFemaleCheck,
		insuredDateOfBirth,
		insuredAge,
		insuredSSN,
		insuredPrimaryPhone,
		insuredEmail,
		conditionalInsuredFields,
		beneficiaryName,
		beneficiaryRelationship,
		agentName,
		agentProducerNumber,
		insuredHospiceYesOrNo,
		dateDay,
		dateMonth,
		dateYear,
		dateFields,
	}
}
